/** Preset library for the broadcaster: list, save as, load, rename, delete. */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createPreset,
  deletePreset,
  listPresets,
  renamePreset,
  type BroadcastLayout,
  type BroadcastPreset,
} from "../../lib/broadcastApi";

interface Props {
  token: string;
  layout: BroadcastLayout;
  /** Replace stage/widgets/callouts with the preset's (version stays server-side). */
  onLoad: (layout: BroadcastLayout) => void;
}

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail ? detail : fallback;
};

const btn =
  "rounded-md border border-slate-500 px-2 py-1 text-xs hover:bg-slate-800 disabled:opacity-40";
const field =
  "flex-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100";

export default function PresetsPanel({ token, layout, onLoad }: Props) {
  const { t } = useTranslation("broadcast");
  const [items, setItems] = useState<BroadcastPreset[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setItems(await listPresets(token));
      setError("");
    } catch (err) {
      setError(errorText(err, t("control.presets.failed")));
    }
  }, [token, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorText(err, t("control.presets.failed")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-700 p-4 space-y-3"
      data-testid="control-presets"
    >
      <h2 className="text-lg font-semibold">{t("control.presets.title")}</h2>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!n) return;
          void run(async () => {
            await createPreset(token, n, layout);
            setName("");
          });
        }}
      >
        <input
          className={field}
          data-testid="control-preset-name"
          placeholder={t("control.presets.name")}
          aria-label={t("control.presets.name")}
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className={btn}
          disabled={busy || !name.trim()}
          data-testid="control-preset-save"
        >
          {t("control.presets.saveAs")}
        </button>
      </form>

      {error && (
        <p
          role="alert"
          data-testid="control-presets-error"
          className="rounded-md border border-red-500 bg-red-950 p-2 text-sm text-red-200"
        >
          {error}
        </p>
      )}

      {items.length === 0 ? (
        <p
          className="text-sm text-slate-300"
          data-testid="control-presets-empty"
        >
          {t("control.presets.empty")}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="control-presets-list">
          {items.map((p) => (
            <li
              key={p.preset_id}
              data-testid={`control-preset-row-${p.preset_id}`}
              className="flex flex-wrap items-center gap-2 rounded-md border border-slate-700 px-2 py-1"
            >
              {renaming?.id === p.preset_id ? (
                <>
                  <input
                    className={field}
                    aria-label={t("control.presets.name")}
                    data-testid={`control-preset-rename-input-${p.preset_id}`}
                    maxLength={60}
                    value={renaming.name}
                    onChange={(e) =>
                      setRenaming({ id: p.preset_id, name: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className={btn}
                    disabled={busy || !renaming.name.trim()}
                    data-testid={`control-preset-rename-save-${p.preset_id}`}
                    onClick={() =>
                      run(async () => {
                        await renamePreset(
                          token,
                          p.preset_id,
                          renaming.name.trim(),
                        );
                        setRenaming(null);
                      })
                    }
                  >
                    {t("control.presets.save")}
                  </button>
                  <button
                    type="button"
                    className={btn}
                    onClick={() => setRenaming(null)}
                  >
                    {t("panel.cancel")}
                  </button>
                </>
              ) : (
                <>
                  <span
                    className="flex-1 truncate text-sm"
                    data-testid={`control-preset-name-${p.preset_id}`}
                  >
                    {p.name}
                  </span>
                  <button
                    type="button"
                    className={btn}
                    data-testid={`control-preset-load-${p.preset_id}`}
                    onClick={() => onLoad(p.layout)}
                  >
                    {t("control.presets.load")}
                  </button>
                  <button
                    type="button"
                    className={btn}
                    data-testid={`control-preset-rename-${p.preset_id}`}
                    onClick={() =>
                      setRenaming({ id: p.preset_id, name: p.name })
                    }
                  >
                    {t("control.presets.rename")}
                  </button>
                  {confirmId === p.preset_id ? (
                    <>
                      <button
                        type="button"
                        className={`${btn} text-red-300`}
                        disabled={busy}
                        data-testid={`control-preset-delete-confirm-${p.preset_id}`}
                        onClick={() =>
                          run(async () => {
                            await deletePreset(token, p.preset_id);
                            setConfirmId(null);
                          })
                        }
                      >
                        {t("control.presets.confirmDelete")}
                      </button>
                      <button
                        type="button"
                        className={btn}
                        onClick={() => setConfirmId(null)}
                      >
                        {t("panel.cancel")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={`${btn} text-red-300`}
                      data-testid={`control-preset-delete-${p.preset_id}`}
                      onClick={() => setConfirmId(p.preset_id)}
                    >
                      {t("control.presets.delete")}
                    </button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
